import { useMemo, type FC, type ReactNode } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper/types';

import 'swiper/css';
import styles from './SceneBannerSlider.module.css';

export type SceneBannerSlide = {
  key: string;
  node: ReactNode;
};

/**
 * Callback fired when the active (= centered / leftmost-fully-visible)
 * slide changes. The Swiper instance is forwarded so callers can inspect
 * `swiper.params.slidesPerView` for mobile-detection gating (e.g.
 * `OverallSceneOverlay` only drives camera transitions when ≤ 1.5 slides
 * are visible at once).
 */
export type SceneBannerSlideChange = (key: string, swiper: SwiperInstance) => void;

type Props = {
  slides: SceneBannerSlide[];
  onActiveSlideChange?: SceneBannerSlideChange;
};

/**
 * Horizontal slider for 2+ cards. Caller passes already-rendered slide
 * content via `slides` — typical contents are `SceneBannerCard compact`
 * (scene-banner, see `MarkdownBlock`).
 *
 * Layout strategy — slidesPerView clamped by `slides.length`:
 *   <600px container  → 1.2 (always 1 + peek, regardless of count)
 *   600-932px         → min(slides.length, 2.2)
 *   933-1399px        → min(slides.length, 3.2)
 *   ≥1400px           → min(slides.length, 4.2)
 *
 * The clamp guarantees: if 2 slides arrive on a wide screen with capacity
 * for 3+, slidesPerView becomes exactly 2 → both cards span the container
 * with no empty space and no fractional peek of nothing. Only when there
 * are more slides than the breakpoint capacity does the fractional `.2`
 * peek appear (= "swipe for more" affordance).
 *
 * `breakpointsBase: 'container'` matches breakpoints against the slider's
 * own width, NOT `window.innerWidth`. This is required for the mobile-
 * preview mode (`?viewMode=mobile`) where the panel sits inside a 394px
 * frame on a 1440px desktop window — viewport-based breakpoints would
 * mis-detect "desktop" and over-scale the cards.
 *
 * Mousewheel: `forceToAxis: true` — only horizontal wheel / trackpad
 * gestures drive the slider; vertical wheel scrolls the parent (chat
 * history / page). `releaseOnEdges: true` — at the start / end of the
 * slider, further wheel input bubbles to the parent instead of being
 * swallowed.
 *
 * Touch behavior: default Swiper `touchAngle: 45°` lets vertical swipes
 * pass through to the parent — e.g. the chat panel's overscroll drag
 * still works when a finger starts on a banner.
 */
export const SceneBannerSlider: FC<Props> = ({ slides, onActiveSlideChange }) => {
  const count = slides.length;
  const breakpoints = useMemo(
    () => ({
      600: { slidesPerView: Math.min(count, 2.2) },
      933: { slidesPerView: Math.min(count, 3.2) },
      1400: { slidesPerView: Math.min(count, 4.2) },
    }),
    [count],
  );

  // Swiper fires `onSlideChange` after the slide-index actually advances
  // (not during drag). Resolve the active slide's stable key from props
  // — `swiper.activeIndex` is just an integer that we map back to the
  // slides[] array.
  const handleSlideChange = useMemo(() => {
    if (!onActiveSlideChange) return undefined;
    return (swiper: SwiperInstance) => {
      const active = slides[swiper.activeIndex];
      if (active) onActiveSlideChange(active.key, swiper);
    };
  }, [slides, onActiveSlideChange]);

  return (
    <Swiper
      modules={[Mousewheel]}
      slidesPerView={Math.min(count, 1.2)}
      breakpoints={breakpoints}
      breakpointsBase="container"
      spaceBetween={8}
      threshold={5}
      mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
      onSlideChange={handleSlideChange}
      className={styles.swiper}
    >
      {slides.map(({ key, node }) => (
        <SwiperSlide key={key} className={styles.slide}>
          {node}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
